import React, { useState, useEffect, useCallback } from 'react';
import { competitionAPI } from '../../api/endpoints';
import { formatXPUNT } from '../../utils/helpers';
import {
  FaTrophy, FaMedal, FaUser, FaChartLine,
  FaCrown, FaSearch, FaArrowLeft, FaUsers,
  FaCalendarAlt, FaStar, FaFire
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './Leaderboard.css';

const Leaderboard = () => {
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [filteredCompetitions, setFilteredCompetitions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Selected competition view
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [standings, setStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);

  // ==================== FETCH JOINED COMPETITIONS ====================
  const fetchJoinedCompetitions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await competitionAPI.getJoined();
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      setCompetitions(list);
      setFilteredCompetitions(list);
    } catch (err) {
      console.error('Failed to fetch joined competitions:', err);
      toast.error('Failed to load your competitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJoinedCompetitions();
  }, [fetchJoinedCompetitions]);

  // Filter by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCompetitions(competitions);
    } else {
      setFilteredCompetitions(
        competitions.filter((c) =>
          (c.title || c.name || '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, competitions]);

  // ==================== OPEN COMPETITION ====================
  const handleOpenCompetition = async (competition) => {
    setSelectedCompetition(competition);
    setStandingsLoading(true);
    try {
      const res = await competitionAPI.getStandings(competition.id);
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.standings)
            ? res.data.standings
            : [];
      setStandings(data);
    } catch (err) {
      console.error('Failed to fetch standings:', err);
      toast.error('Failed to load standings');
      setStandings([]);
    } finally {
      setStandingsLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedCompetition(null);
    setStandings([]);
  };

  // ==================== RANK HELPERS ====================
  const getRankIcon = (rank) => {
    if (rank === 1) return <FaCrown className="rank-icon gold" />;
    if (rank === 2) return <FaMedal className="rank-icon silver" />;
    if (rank === 3) return <FaMedal className="rank-icon bronze" />;
    return <span className="rank-number">#{rank}</span>;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="leaderboard-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // ==================== COMPETITION STANDINGS VIEW ====================
  if (selectedCompetition) {
    const comp = selectedCompetition;
    return (
      <div className="leaderboard-page">
        <button className="back-button" onClick={handleBack}>
          <FaArrowLeft /> Back to Competitions
        </button>

        <div className="competition-header">
          <h1>
            <FaTrophy className="header-icon" />
            {comp.title || comp.name}
          </h1>
          <div className="competition-meta">
            {comp.type && <span className="meta-pill">{comp.type}</span>}
            {comp.status && (
              <span className={`meta-pill status-${comp.status}`}>
                {comp.status}
              </span>
            )}
            {comp.entry_fee !== undefined && (
              <span className="meta-pill">
                Entry: {formatXPUNT(comp.entry_fee)}
              </span>
            )}
            {comp.participants_count !== undefined && (
              <span className="meta-pill">
                <FaUsers /> {comp.participants_count} players
              </span>
            )}
          </div>
          {comp.description && (
            <p className="competition-description">{comp.description}</p>
          )}
        </div>

        {standingsLoading ? (
          <div className="standings-loading">
            <LoadingSpinner size="large" />
          </div>
        ) : standings.length === 0 ? (
          <div className="empty-state">
            <FaTrophy className="empty-icon" />
            <p>No standings yet</p>
            <small>Predictions will appear here once submitted</small>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {standings.length >= 3 && (
              <div className="podium">
                <div className="podium-item second">
                  <div className="podium-avatar">
                    {standings[1]?.name?.[0] || '?'}
                  </div>
                  <span className="podium-rank">🥈</span>
                  <span className="podium-name">
                    {standings[1]?.name || 'Unknown'}
                  </span>
                  <span className="podium-points">
                    {standings[1]?.points || 0} pts
                  </span>
                  <div className="podium-bar second" style={{ height: '60%' }} />
                </div>
                <div className="podium-item first">
                  <div className="podium-avatar">
                    {standings[0]?.name?.[0] || '?'}
                  </div>
                  <span className="podium-rank">👑</span>
                  <span className="podium-name">
                    {standings[0]?.name || 'Unknown'}
                  </span>
                  <span className="podium-points">
                    {standings[0]?.points || 0} pts
                  </span>
                  <div className="podium-bar first" style={{ height: '100%' }} />
                </div>
                <div className="podium-item third">
                  <div className="podium-avatar">
                    {standings[2]?.name?.[0] || '?'}
                  </div>
                  <span className="podium-rank">🥉</span>
                  <span className="podium-name">
                    {standings[2]?.name || 'Unknown'}
                  </span>
                  <span className="podium-points">
                    {standings[2]?.points || 0} pts
                  </span>
                  <div className="podium-bar third" style={{ height: '40%' }} />
                </div>
              </div>
            )}

            {/* Full standings table */}
            <div className="leaderboard-list">
              <div className="list-header">
                <span className="col-rank">Rank</span>
                <span className="col-user">User</span>
                <span className="col-points">Points</span>
                <span className="col-wins">Correct</span>
                <span className="col-winrate">Accuracy</span>
                <span className="col-winnings">Winnings</span>
              </div>

              {standings.map((item, idx) => {
                const rank = item.rank ?? idx + 1;
                return (
                  <div
                    key={item.id ?? item.user_id ?? idx}
                    className={`list-item ${getRankBadge(rank)}`}
                  >
                    <span className="col-rank">{getRankIcon(rank)}</span>
                    <span className="col-user">
                      <div className="user-info">
                        <div className="user-avatar-small">
                          {item.name?.[0] || '?'}
                        </div>
                        <span>{item.name || 'Unknown'}</span>
                      </div>
                    </span>
                    <span className="col-points">{item.points ?? 0}</span>
                    <span className="col-wins">
                      {item.correct_predictions ?? item.wins ?? 0}
                      {item.total_predictions !== undefined && (
                        <span className="muted">
                          {' '}/ {item.total_predictions}
                        </span>
                      )}
                    </span>
                    <span className="col-winrate">
                      {item.accuracy !== undefined
                        ? `${item.accuracy}%`
                        : `${item.win_rate || 0}%`}
                    </span>
                    <span className="col-winnings">
                      {formatXPUNT(item.total_winnings || item.winnings || 0)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // ==================== COMPETITION LIST VIEW ====================
  return (
    <div className="leaderboard-page">
      <div className="page-header">
        <div>
          <h1>
            <FaTrophy className="header-icon" /> My Competitions
          </h1>
          <p className="page-subtitle">
            Select a competition to view its standings
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search competitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Competition list */}
      {filteredCompetitions.length === 0 ? (
        <div className="empty-state">
          <FaTrophy className="empty-icon" />
          <p>
            {competitions.length === 0
              ? "You haven't joined any competitions yet"
              : 'No competitions match your search'}
          </p>
          {competitions.length === 0 && (
            <small>Join a competition to see it here</small>
          )}
        </div>
      ) : (
        <div className="competition-grid">
          {filteredCompetitions.map((comp) => (
            <button
              key={comp.id}
              className="competition-card"
              onClick={() => handleOpenCompetition(comp)}
              type="button"
            >
              <div className="competition-card-header">
                <span className="competition-type">
                  {comp.type || 'Competition'}
                </span>
                {comp.status && (
                  <span className={`competition-status status-${comp.status}`}>
                    {comp.status}
                  </span>
                )}
              </div>

              <h3 className="competition-title">
                {comp.title || comp.name || 'Untitled Competition'}
              </h3>

              {comp.description && (
                <p className="competition-desc">{comp.description}</p>
              )}

              <div className="competition-stats">
                {comp.participants_count !== undefined && (
                  <span>
                    <FaUsers /> {comp.participants_count}
                  </span>
                )}
                {comp.entry_fee !== undefined && (
                  <span>
                    <FaStar /> {formatXPUNT(comp.entry_fee)}
                  </span>
                )}
                {comp.prize_pool !== undefined && (
                  <span>
                    <FaTrophy /> {formatXPUNT(comp.prize_pool)}
                  </span>
                )}
                {comp.created_at && (
                  <span>
                    <FaCalendarAlt /> {formatDate(comp.created_at)}
                  </span>
                )}
              </div>

              <div className="competition-card-footer">
                <span className="view-standings">
                  View Standings <FaArrowLeft className="rotate-180" />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;